import { useCallback, useMemo, useState } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import DateFilter, { DateRangePreset } from "../activity/date-filter"
import { columnSeries, getColumnChartOptions } from "./options"
import { useFetchDashboardPerformanceStatsQuery } from "../../../redux/queries"
import If from "../if"
import PerformanceSkeleton from "../skeletons/dashboard/performance"
import StatItem from "./stat-item"
import { DateTime } from "luxon"

interface Props {
  title: string
  type: string
  orgId?: string
  defaultDays?: 7 | 30 | 90
}

const ChartWithStats = ({
  title,
  type,
  orgId = "",
  defaultDays = 90
}: Props) => {
  const [dateRange, setDateRange] = useState<DateRangePreset>(
    defaultDays === 90 ? "last_90" : defaultDays === 30 ? "last_30" : "last_7"
  )
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null)
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null)

  // Convert DateRangePreset to numeric days for API
  const days = useMemo(() => {
    switch (dateRange) {
      case "today":
        return 1
      case "last_7":
        return 7
      case "last_30":
        return 30
      case "last_90":
        return 90
      case "custom":
        if (customStartDate && customEndDate) {
          const start = DateTime.fromJSDate(customStartDate)
          const end = DateTime.fromJSDate(customEndDate)
          return Math.ceil(end.diff(start, "days").days)
        }
        return 30
      case "all":
      default:
        return 1000
    }
  }, [dateRange, customStartDate, customEndDate])

  const { data, isFetching } = useFetchDashboardPerformanceStatsQuery({
    orgId,
    range: days,
    type
  })

  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      setDateRange(range)
      setCustomStartDate(startDate || null)
      setCustomEndDate(endDate || null)
    },
    []
  )

  const chatLabels = data?.chartData
    ?.map(item => ({
      date: DateTime.fromFormat(item.period_start, "yyyy-LL-dd"),
      y: Math.floor(
        "conversion_rate" in item ? item.conversion_rate : item.opened
      ),
      color: "#786ff6"
    }))
    .sort((a, b) => (a.date > b.date ? 1 : -1))
    .map(item => ({
      ...item,
      name: item.date.toFormat("LL/dd/yyyy")
    }))

  const chartOptions = {
    ...getColumnChartOptions(data?.type ?? ""),
    series: [{ ...columnSeries, data: chatLabels }]
  }

  return (
    <If
      condition={isFetching}
      then={<PerformanceSkeleton />}
      else={
        <div className="bg-white flex-1 p-4 md:p-6 rounded-md overflow-hidden">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium">{title}</h3>

            <DateFilter
              selectedRange={dateRange}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onRangeChange={handleDateRangeChange}
              hideLabelOnMobile
            />
          </div>
          <div className="py-6">
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </div>
          <div className="flex items-center gap-6 px-0 md:px-8">
            {data?.stats?.map(({ key, label, value }) => (
              <StatItem key={key} label={label} value={value} />
            ))}
          </div>
        </div>
      }
    />
  )
}

export default ChartWithStats
